import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import constants from "../../Constants";
import cookie from "react-cookies";
import debounce from "lodash/debounce";
import "./OrderSearch.css";

const baseURL = constants.baseURL;

const OrderSearch = ({
  onOrderSelect,
  filterOrders,
  ordersFilter,
  setOPaginator,
  setProcessedFilter,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchRef = useRef(null);

  const suggestions = [
    { text: "Try searching by order ID", icon: "mdi-identifier" },
    { text: "Search by customer name", icon: "mdi-account" },
    { text: "Search by vehicle type", icon: "mdi-truck" },
    { text: "Search by delivery date", icon: "mdi-calendar" },
  ];

  const getImage = (trucktype) => {
    try {
      return `https://media.qiktruck.co.za/3d-${trucktype}.png`;
    } catch (error) {
      return "";
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        if (!term || term.length < 2) {
          setSearchResults([]);
          return;
        }

        setLoading(true);
        axios
          .get(
            `${baseURL}/customer-search-orders?query=${encodeURIComponent(term)}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: cookie.load("session-id-qt"),
              },
            }
          )
          .then((response) => {
            setSearchResults(response.data);
          })
          .catch((error) => {
            console.error("Search error:", error);
            setSearchResults([]);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 300),
    [setSearchResults, setLoading]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // Handle clicks outside the search component
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setOpenDropdown(false);
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setOpenDropdown(true);

    // Also update the parent component's filter state (if provided)
    if (filterOrders) {
      filterOrders({
        ...(ordersFilter || {}),
        search: value,
      });
    }
    if (setOPaginator) setOPaginator(0);
  };

  const handleSelectOrder = (order) => {
    if (filterOrders) {
      filterOrders({
        ...(ordersFilter || {}),
        search: order.orderid,
        orderstatus: order.orderstatus,
        vehicleid: "all",
      });
    }
    if (setOPaginator) setOPaginator(0);
    setSearchTerm(order.orderid);
    if (setProcessedFilter) setProcessedFilter("");

    if (onOrderSelect) {
      onOrderSelect(order);
    } else if (typeof window !== "undefined") {
      // When used outside the dashboard (e.g. in the navbar), update the
      // URL and either notify the dashboard (if already on it) or redirect.
      const isOnDashboard = window.location.pathname.startsWith("/dashboard");
      if (isOnDashboard) {
        try {
          window.history.replaceState({}, "", "/dashboard");
        } catch (_e) {
          // ignore
        }
        window.dispatchEvent(
          new CustomEvent("qt-search-order", { detail: order })
        );
      } else {
        window.location.href = "/dashboard?orderid=" + order.orderid;
      }
    }
    // Don't close dropdown immediately to avoid jarring UX
    setTimeout(() => setOpenDropdown(false), 300);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setOpenDropdown(true);
  };

  const formatDate = (date, time) => {
    if (!date) return "";
    return `${date} ${time || ""}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "assigned":
        return "mdi-truck-check";
      case "started":
        return "mdi-truck-fast";
      case "arrived":
        return "mdi-map-marker-check";
      case "finished":
        return "mdi-check-circle";
      default:
        return "mdi-truck";
    }
  };

  const showSuggestions = isFocused && !searchTerm && suggestions.length > 0;
  const showResults =
    searchTerm.length > 1 && openDropdown && searchResults.length > 0;
  const showNoResults =
    searchTerm.length > 1 &&
    openDropdown &&
    searchResults.length === 0 &&
    !loading;

  return (
    <div className="search-parent" ref={searchRef}>
      <div className="search-input-wrapper">
        <input
          placeholder="Track your orders"
          className="search main-search search-input"
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus}
        />
        {searchTerm && (
          <button
            className="clear-search"
            onClick={() => {
              setSearchTerm("");
              handleSearchChange({ target: { value: "" } });
              setOpenDropdown(true);
            }}
          >
            <i className="mdi mdi-close"></i>
          </button>
        )}

        {loading && (
          <div className="search-loading">
            <span className="loading-spinner"></span>
          </div>
        )}
      </div>

      {showResults && (
        <div className="search-results-container">
          <div className="search-results-header">
            <i className="mdi mdi-magnify"></i>
            <span>Search Results ({searchResults.length})</span>
          </div>
          <div className="search-results-list">
            {searchResults.map((order) => (
              <div
                key={order.orderid}
                className="search-result-row"
                onClick={() => handleSelectOrder(order)}
                role="button"
                tabIndex={0}
              >
                <div className="result-col result-col-id">
                  <div className="order-id">
                    {order.orderid.split("-")[0]}
                  </div>
                </div>
                <div className="result-col result-col-date">
                  <i className="mdi mdi-calendar-clock"></i>
                  <span>
                    {formatDate(order.collectiondate, order.collectiontime)}
                  </span>
                </div>
                <div className="result-col result-col-truck">
                  <img
                    src={getImage(order.trucktype)}
                    alt="Truck"
                    height={24}
                  />
                </div>
                <div className="result-col result-col-status">
                  <div
                    className={`order-status status-${order.orderstatus}`}
                  >
                    <i
                      className={`mdi ${getStatusIcon(order.orderstatus)}`}
                    ></i>
                    <span>{order.orderstatus}</span>
                  </div>
                </div>
                <div className="result-col result-col-customer">
                  <i className="mdi mdi-account"></i>
                  <span>
                    {order.firstname} {order.lastname}
                  </span>
                </div>
                <div className="result-col result-col-desc">
                  <i className="mdi mdi-package-variant"></i>
                  <span className="result-desc-text">
                    {order.description}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showNoResults && (
        <div className="search-results-container">
          <div className="no-results">
            <i className="mdi mdi-magnify-close"></i>
            <span>No results found for "{searchTerm}"</span>
            <p>Try a different search term or refine your query</p>
          </div>
        </div>
      )}

      {showSuggestions && (
        <div className="search-results-container suggestions-container">
          <div className="search-results-header">
            <i className="mdi mdi-lightbulb-on"></i>
            <span>Search Suggestions</span>
          </div>
          <div className="search-suggestions">
            {suggestions.map((suggestion, index) => (
              <div className="suggestion-item" key={index}>
                <i className={`mdi ${suggestion.icon}`}></i>
                <span>{suggestion.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderSearch;
